/**
 * Content-addressed URLs — the class of response that is safe to cache forever.
 *
 * A name that is the hash of its bytes cannot go stale and cannot come to mean
 * something else, so it needs no freshness rule, no version bump and no
 * directory classification: it is immune by construction to the failure that
 * shut the service-worker cache down (path-shaped policy going stale as paths
 * moved). This module says which same-origin routes are in that class and how
 * a body is held to its name — on every read and before every write, because
 * content addressing only helps if something checks it.
 *
 * Used by sw.js (a URL-keyed cache ahead of the alwaysFetch kill switch, for
 * every acequia.js app) alongside chains.js's own hash-keyed link cache, which
 * serves the verifier inside the service worker itself. Project:
 * local-first-chain-verification-for-peer-origins, sketch step 3.
 */

/**
 * `check` says how bytes are held to the name:
 *   'sha256'     — last path segment is the SHA-256 hex of the body (anchors)
 *   'thumbprint' — kid segment is `server-` + RFC 7638 JWK thumbprint of the
 *                  returned publicKey (server signing keys; only a self-naming
 *                  kid matches — the role-named `server-{domain}` does not,
 *                  and must not, since it changes on rotation)
 *   null         — not self-naming. Revocation objects are served under the
 *                  hash of the token they revoke, not of their own bytes, and
 *                  are cached on the server's overwrite refusal + `immutable`
 *                  header alone (owner decision 2026-09-04). This class may
 *                  need an invalidation path, or renaming, later.
 */
export const CONTENT_ADDRESSED_RULES = Object.freeze([
    { id: 'anchor', re: /^\/auth\/anchors\/([a-f0-9]{64})$/, check: 'sha256' },
    { id: 'server-key', re: /^\/auth\/server-keys\/server-([A-Za-z0-9_-]{43})$/, check: 'thumbprint' },
    { id: 'revocation', re: /^\/auth\/revocations\/([a-f0-9]{64})$/, check: null },
])

/**
 * Classify a URL. Returns `{ rule, name, key }` or null.
 *
 * `key` is origin + pathname: the name is the whole identity, so a query
 * string is not part of it. When `sameOriginAs` is given, a URL on any other
 * origin is not matched — the rules describe this domain's routes, not the
 * world's.
 *
 * @param {string} url
 * @param {string} [sameOriginAs] - e.g. self.location.origin
 */
export function matchContentAddressed(url, sameOriginAs) {
    let u
    try {
        u = new URL(url)
    } catch {
        return null
    }
    if (sameOriginAs && u.origin !== sameOriginAs) return null
    for (const rule of CONTENT_ADDRESSED_RULES) {
        const m = u.pathname.match(rule.re)
        if (m) return { rule, name: m[1], key: u.origin + u.pathname }
    }
    return null
}

export async function sha256Hex(text) {
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))
    return Array.from(new Uint8Array(buf))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('')
}

/**
 * RFC 7638 JWK thumbprint: SHA-256 over the required members in lexical
 * order with no whitespace, base64url without padding. Hand-rolled rather than
 * jose's so the check has no dependency on the bundle being initialised;
 * pinned against jose in test/content-addressed.test.mjs.
 *
 * @returns {Promise<string|null>} null for a JWK it cannot thumbprint
 */
export async function jwkThumbprint(jwk) {
    const members = {
        RSA: ['e', 'kty', 'n'],
        EC: ['crv', 'kty', 'x', 'y'],
        OKP: ['crv', 'kty', 'x'],
    }[jwk?.kty]
    if (!members || members.some((k) => typeof jwk[k] !== 'string')) return null
    const canonical =
        '{' + members.map((k) => `${JSON.stringify(k)}:${JSON.stringify(jwk[k])}`).join(',') + '}'
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(canonical))
    let bin = ''
    for (const b of new Uint8Array(buf)) bin += String.fromCharCode(b)
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * Do these bytes name themselves under this rule? Anything that throws is
 * "no": an entry that cannot be checked is unusable, not believed.
 */
export async function bodyNamesItself(rule, name, text) {
    try {
        if (rule.check === null) return true
        if (rule.check === 'sha256') return (await sha256Hex(text)) === name
        if (rule.check === 'thumbprint') {
            const data = JSON.parse(text)
            return !!data?.publicKey && (await jwkThumbprint(data.publicKey)) === name
        }
    } catch {
        /* unusable */
    }
    return false
}
